import React, { useState, useRef, useEffect } from 'react';
import { Layout, Input, Button, Typography, message, Tour } from 'antd';
import { AudioOutlined, AudioMutedOutlined } from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { Modal, Checkbox } from 'antd';
import { languageOptions } from './variables';
import { useTranslation } from 'react-i18next';

const { Header } = Layout;
const { Title } = Typography;

const TopBar = ({ user, currentMeetingId, setCurrentMeetingId }) => {
    const [MeetingLink, setMeetingLink] = useState('');
    const [loading, setLoading] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [userToken, setUserToken] = useState('');
    const [profile, setProfile] = useState(null);
    const [isLanguageModalVisible, setIsLanguageModalVisible] = useState(false);
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const { t, i18n } = useTranslation();
    const [openTour, setOpenTour] = useState(false);

    const location = useLocation();
    const navigate = useNavigate();
    const mediaRecorderRef = useRef(null);
    const websocketRef = useRef(null);

    useEffect(() => {
        const fetchSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            const token = session?.access_token;
            setUserToken(token);
        };
        fetchSession();
    }, []);


    useEffect(() => {
        if (user) {
            const hasSeenTutorial = localStorage.getItem(`tutorial_shown_${user.id}`);
            if (!hasSeenTutorial) {
                setOpenTour(true);
                localStorage.setItem(`tutorial_shown_${user.id}`, 'true');
            }
        }
    }, [user]);



    useEffect(() => {
        const fetchProfile = async () => {
            if (user) {
                const { data, error } = await supabase
                    .from('profiles')
                    .select('*')
                    .eq('id', user.id)
                    .single();

                if (error) {
                    console.error('Error fetching profile:', error);
                } else {
                    setProfile(data);
                }
            }
        };
        fetchProfile();
    }, [user]);


    const showLanguageModal = () => {
        if (profile && profile.main_languages) {
            setSelectedLanguages(JSON.parse(profile.main_languages));
        }
        setIsLanguageModalVisible(true);
    };

    const handleInputChange = (e) => {
        setMeetingLink(e.target.value);
    };

    const handleStartRecording = async () => {
        showLanguageModal();
    };


    const steps = [
        {
            title: 'Install Chrome Extension',
            description: 'To use ELI, you need to install the Chrome extension first. Click here to install!',
            target: () => document.querySelector('[data-tour="extension-button"]'),
            nextButtonProps: {
                children: 'Next',
            },
            prevButtonProps: {
                children: 'Previous',
            },
        },
        {
            title: 'Join Our Community',
            description: "We'd love to hear about your experiences with ELI on our discord!",
            target: () => document.querySelector('[data-tour="discord"]'),
            nextButtonProps: {
                children: 'Done',
            },
            prevButtonProps: {
                children: 'Previous',
            },
        }
    ];

    const startRecordingWithLanguages = async () => {
        setLoading(true);
        try {
            const { data, error } = await supabase
                .from('meetings')
                .insert([
                    {
                        user_id: user.id,
                        title: `${t("Meeting Note")} - ${new Date().toLocaleString()}`,
                        status: 'ongoing',
                        record_method: 'direct_record',
                        meeting_languages: JSON.stringify(selectedLanguages)
                    }
                ])
                .select()
                .single();

            if (error) throw error;

            setCurrentMeetingId(data.id);
            message.success(t("Recording has started."));
            navigate(`/meeting/${data.id}`);
            startWebSocketAndRecording(data.id);

        } catch (error) {
            console.error(t("Error creating meeting:"), error);
            message.error(t("Failed to start recording."));
        } finally {
            setLoading(false);
        }
    };


    // const handleStartRecording = async () => {
    //     setLoading(true);
    //     try {
    //         const { data, error } = await supabase
    //             .from('meetings')
    //             .insert([
    //                 {
    //                     user_id: user.id,
    //                     title: `Meeting Note - ${new Date().toLocaleString()}`,
    //                     status: 'ongoing',
    //                     record_method: 'direct_record'
    //                 }
    //             ])
    //             .select()
    //             .single();

    //         if (error) throw error;

    //         setCurrentMeetingId(data.id);
    //         message.success('녹음이 시작되었습니다.');
    //         navigate(`/meeting/${data.id}`);
    //         startWebSocketAndRecording(data.id);

    //     } catch (error) {
    //         console.error('Error creating meeting:', error);
    //         message.error('녹음 시작에 실패했습니다.');
    //     } finally {
    //         setLoading(false);
    //     }
    // };

    const handleStopRecording = async () => {
        setLoading(true);
        try {
            const { error } = await supabase
                .from('meetings')
                .update({
                    status: 'finished',
                    ended_at: new Date().toISOString()
                })
                .eq('id', currentMeetingId);

            if (error) throw error;

            setCurrentMeetingId(null);
            message.success(t("Recording has been stopped."));
            window.location.href = `/meeting/${currentMeetingId}`;
            stopWebSocketAndRecording();

        } catch (error) {
            console.error('Error stopping meeting:', error);
            message.error(t("Failed to stop recording."));
        } finally {
            setLoading(false);
        }
    };


    const startWebSocketAndRecording = (meetingId) => {
        // WebSocket 연결
        websocketRef.current = new WebSocket(`${process.env.REACT_APP_BACKEND_URL}/ws/${meetingId}`);

        // 유저 토큰을 헤더에 추가
        websocketRef.current.onopen = () => {
            console.log(t("WebSocket connected"));

            websocketRef.current.send(JSON.stringify({ type: 'token', token: userToken }));

            navigator.mediaDevices.getUserMedia({ audio: true })
                .then(stream => {
                    const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
                    const source = audioContext.createMediaStreamSource(stream);
                    const processor = audioContext.createScriptProcessor(4096, 1, 1);

                    source.connect(processor);
                    processor.connect(audioContext.destination);

                    processor.onaudioprocess = e => {
                        if (websocketRef.current.readyState === WebSocket.OPEN) {
                            const inputData = e.inputBuffer.getChannelData(0);
                            const buffer = new ArrayBuffer(inputData.length * 2);
                            const outputData = new DataView(buffer);
                            for (let i = 0; i < inputData.length; i++) {
                                let s = Math.max(-1, Math.min(1, inputData[i]));
                                outputData.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
                            }
                            websocketRef.current.send(buffer);
                        }
                    };

                    setIsRecording(true);
                })
                .catch(err => {
                    console.error(t("Failed to get audio stream:"), err);
                    message.error(t("Failed to access microphone."));
                });
        };

        websocketRef.current.onerror = (error) => {
            console.error(t("WebSocket error:"), error);
            message.error(t("Failed to connect to WebSocket."));
        };
    };

    const stopWebSocketAndRecording = () => {
        if (mediaRecorderRef.current && isRecording) {
            mediaRecorderRef.current.stop();
            setIsRecording(false);
        }
        if (websocketRef.current) {
            websocketRef.current.close();
        }
    };

    const getPageName = () => {
        switch (location.pathname) {
            case '/':
                return 'My Meetings';
            case '/settings':
                return 'Settings';
            case '/login':
                return 'Login';
            case '/signup':
                return 'Sign Up';
            case '/privacy':
                return 'Privacy';
            case '/term':
                return 'Term & Policy';
            case '/agent/list':
                return 'Assistant List';
            case '/agent/create':
                return 'Assistant Create';
            default:
                return 'Meeting Detail';
        }
    };

    const renderPageSpecificContent = () => {
        if (!user) {
            return null; // Don't render anything if user is not logged in
        }

        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <Input
                    placeholder="Meeting Link that needs help"
                    value={MeetingLink}
                    onChange={handleInputChange}
                    style={{ width: 300, marginRight: 16 }}
                />
                <Button
                    type={currentMeetingId ? "danger" : "primary"}
                    icon={currentMeetingId ? <AudioMutedOutlined /> : <AudioOutlined />}
                    onClick={currentMeetingId ? handleStopRecording : handleStartRecording}
                    loading={loading}
                >
                    {currentMeetingId ? t("Stop Recording") : t("Start Recording")}
                </Button>
            </div>
        );
    };

    const LanguageSelectionModal = () => (
        <Modal
            title="Select Languages for Meeting"
            visible={isLanguageModalVisible}
            onOk={() => {
                setIsLanguageModalVisible(false);
                startRecordingWithLanguages();
            }}
            onCancel={() => setIsLanguageModalVisible(false)}
        >
            <Checkbox.Group
                options={languageOptions.map(lang => ({ label: lang.name, value: lang.code }))}
                value={selectedLanguages}
                onChange={setSelectedLanguages}
            />
            <p style={{ marginTop: '10px', color: 'gray' }}>* It will automatically detect and start the transcript among the selected languages.</p>
        </Modal>
    );

    const handleInstallExtension = () => {
        // 크롬 익스텐션 스토어 URL로 변경해주세요
        window.open('https://chromewebstore.google.com/detail/eli-realtime-meeting-copi/jlcdehhmjnofkofdgelhabdfiaodiljp?authuser=0&hl=ko&pli=1', '_blank');
    };

    return (
        <Header
            style={{
                background: '#fff',
                padding: '0 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '1px solid #f0f0f0',
                boxShadow: '0 1px 4px rgba(0, 0, 0, 0.08)',
                position: 'fixed',
                top: 0,
                left: 250,  // Navigation 컴포넌트의 너비만큼 왼쪽에서 띄웁니다
                right: 0,
                zIndex: 1000
            }}
        >
            <Title level={4} style={{ margin: 0 }}>{getPageName()}</Title>
            {/* {renderPageSpecificContent()} */}
            <Button
                type="primary"
                onClick={handleInstallExtension}
                data-tour="extension-button"  // Tour를 위한 데이터 속성 추가
            >
                {t("Install Chrome Extension")}
            </Button>
            <LanguageSelectionModal />
            <Tour
                open={openTour}
                onClose={() => setOpenTour(false)}
                steps={steps}
                type="primary"
            />
        </Header>
    );
};

export default TopBar;